/* eslint-disable react/destructuring-assignment */
/* eslint-disable react/require-default-props */
/* eslint-disable react-hooks/exhaustive-deps */
import { ActionIcon, Grid, NumberInput, Tooltip } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconCurrencyReal, IconExclamationCircle, IconHelp, IconTax } from '@tabler/icons-react';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { Financial } from '../utils/constants.utils';
import { formatCurrency } from '../utils/formatter.utils';
import { isNullOrEmpty } from '../utils/helper.utils';
import PageViewProperty from './core/PageViewProperty/PageViewProperty';

export type BasicPriceTaxData = {
  preco: number | null;
  imposto: number | null;
};

export type BasicPriceTaxRef = {
  validate(): BasicPriceTaxData | null;
  clear(): void;
};

type BasicPriceTaxProps = {
  isEdit: boolean;
  disabled?: boolean;
  requiredPrice?: boolean;
  disabledPrice?: boolean;
  requiredTax?: boolean;
  disabledTax?: boolean;
  labelPreco?: string;
  labelImposto?: string;
  showTax?: boolean;
  value: BasicPriceTaxData;
  zIndex: number;
};

function formatNumber(value: number | null): string {
  if (isNullOrEmpty(value)) {
    return '-';
  }
  return Number(value).toFixed(2);
}

const BasicPriceTax = forwardRef((props: BasicPriceTaxProps, ref) => {
  const showTax = props.showTax ?? true;
  const canToggleUnknownPrice = props.isEdit && !props.requiredPrice && !props.disabledPrice;
  const [unknownPrice, setUnknownPrice] = useState<boolean>(
    canToggleUnknownPrice && isNullOrEmpty(props.value.preco)
  );

  const form = useForm<BasicPriceTaxData>({
    initialValues: {
      preco: props.value.preco ?? (props.requiredPrice ? 0 : (undefined as never as null)),
      imposto: props.value.imposto ?? Financial.Tax,
    },
    validate: {
      preco: (value) => {
        if (
          (!props.requiredPrice || props.disabled) &&
          ((unknownPrice && isNullOrEmpty(value)) || (!unknownPrice && !isNullOrEmpty(value)))
        ) {
          return null;
        }
        return isNullOrEmpty(value) || Number.isNaN(value) ? 'Campo obrigatorio' : null;
      },
      imposto: (value) => {
        if (!showTax || !props.requiredTax || props.disabled) {
          return null;
        }
        return isNullOrEmpty(value) || Number.isNaN(value) ? 'Campo obrigatorio' : null;
      },
    },
  });

  const togglePriceValue = () => {
    let newValue;
    if (unknownPrice) {
      newValue = 0;
    } else {
      newValue = undefined as never as null;
    }
    setUnknownPrice(!unknownPrice);
    form.setFieldValue('preco', newValue);
  };

  useEffect(() => {
    const nextUnknownPrice = canToggleUnknownPrice && isNullOrEmpty(props.value.preco);
    setUnknownPrice(nextUnknownPrice);

    form.setValues({
      preco: props.value.preco ?? (props.requiredPrice ? 0 : (undefined as never as null)),
      imposto: props.value.imposto ?? Financial.Tax,
    });
    form.clearErrors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.value.preco, props.value.imposto, props.requiredPrice, canToggleUnknownPrice]);

  useImperativeHandle(ref, () => ({
    validate() {
      if (form.validate().hasErrors) {
        return null;
      }

      return {
        preco: form.values.preco ?? null,
        imposto: showTax ? form.values.imposto : null,
      };
    },
    clear() {
      form.setValues({
        preco: props.requiredPrice ? 0 : (undefined as never as null),
        imposto: props.requiredTax ? Financial.Tax : (undefined as never as null),
      });
    },
  }));

  return (
    <Grid columns={showTax ? 2 : 1}>
      <Grid.Col span={1}>
        {props.isEdit ? (
          <NumberInput
            leftSection={<IconCurrencyReal size={15} />}
            rightSectionPointerEvents="all"
            rightSection={
              canToggleUnknownPrice ? (
                <Tooltip
                  label={unknownPrice ? 'Valor desconhecido' : 'Valor informado'}
                  withArrow
                  zIndex={props.zIndex + 1}
                >
                  <ActionIcon
                    size="sm"
                    radius="sm"
                    color={unknownPrice ? 'gray' : 'orange'}
                    variant="outline"
                    onClick={togglePriceValue}
                    disabled={props.disabled}
                    mt={1}
                    mr={8}
                  >
                    {unknownPrice ? <IconHelp size={15} /> : <IconExclamationCircle size={15} />}
                  </ActionIcon>
                </Tooltip>
              ) : null
            }
            label={props.labelPreco || 'Preço'}
            placeholder={props.labelPreco || 'Preço'}
            decimalScale={2}
            step={0.1}
            stepHoldDelay={500}
            stepHoldInterval={(t) => Math.max(1000 / t ** 2, 25)}
            required={props.requiredPrice || !unknownPrice}
            hideControls={props.disabled || props.disabledPrice || unknownPrice}
            disabled={props.disabled || props.disabledPrice || unknownPrice}
            {...form.getInputProps('preco')}
          />
        ) : (
          <PageViewProperty
            label={props.labelPreco || 'Preço'}
            text={isNullOrEmpty(form.values.preco) ? '-' : formatCurrency(form.values.preco!)}
            size="sm"
          />
        )}
      </Grid.Col>
      {showTax && (
        <Grid.Col span={1}>
          {props.isEdit ? (
            <NumberInput
              leftSection={<IconTax size={15} />}
              rightSection="%"
              label={props.labelImposto || 'Imposto'}
              placeholder={props.labelImposto || 'Imposto'}
              decimalScale={2}
              hideControls
              // step={0.1}
              // stepHoldDelay={500}
              // stepHoldInterval={(t) => Math.max(1000 / t ** 2, 25)}
              required={props.requiredTax}
              disabled={props.disabled || props.disabledTax}
              {...form.getInputProps('imposto')}
            />
          ) : (
            <PageViewProperty
              label={props.labelImposto || 'Imposto'}
              text={form.values.imposto === null ? '-' : `${formatNumber(form.values.imposto)}%`}
              size="sm"
            />
          )}
        </Grid.Col>
      )}
    </Grid>
  );
});

export default BasicPriceTax;
